import { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import './NewMessage.css';

const NewMessage = () => {
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [messageGroups, setMessageGroups] = useState([]);
  const [selectedGroupOption, setSelectedGroupOption] = useState(null);
  const [loadingGroupRecipients, setLoadingGroupRecipients] = useState(false);
  const [groupFeedback, setGroupFeedback] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const [searchParams] = useSearchParams();
  const replyId = searchParams.get('reply');

  axios.defaults.withCredentials = true;

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 30);

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/messages/myrecipients`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.data && Array.isArray(res.data.data)) {
          const sorted = [...res.data.data].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '')
          );
          setRecipients(sorted);
        } else {
          setRecipients([]);
          setError('Geen ontvangers gevonden');
        }
      })
      .catch(() => {
        setError('Kan ontvangers niet laden');
        setRecipients([]);
      });

    if (!replyId) {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/messages/mygroups`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const groups = Array.isArray(res.data?.data) ? res.data.data : [];
          setMessageGroups(groups);
        })
        .catch((err) => {
          // Groepsselectie is een extra hulpmiddel. De bestaande ontvangerslijst
          // blijft gewoon bruikbaar als dit endpoint onverhoopt niet beschikbaar is.
          console.error('Kon berichtgroepen niet laden:', err);
          setMessageGroups([]);
        });
    }

    return () => clearTimeout(timer);
  }, [token, replyId]);

  useEffect(() => {
    if (!replyId) return;

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/messages/getmessage/${replyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const original = res.data;
        setSelectedIds([original.sender_id]);
        setSubject((prev) => prev || `Re: ${original.subject || '(geen onderwerp)'}`);
        const quoted = `\n\n----- Oorspronkelijk bericht van ${original.sender_name} op ${new Date(
          original.created_at
        ).toLocaleString('nl-NL')} -----\n${original.body}`;
        setBody((prev) => prev || quoted);
      })
      .catch((err) => console.error('Kon origineel bericht niet ophalen:', err));
  }, [replyId, token]);

  const formatRecipientLabel = (user) => {
    if (['super_admin', 'locatie_admin'].includes(user.role)) {
      return user.name;
    }

    return `${user.name} (${user.role_label || user.role})`;
  };

  const handleGroupSelect = async (option) => {
    if (!option?.value || loadingGroupRecipients) return;

    setSelectedGroupOption(option);
    setLoadingGroupRecipients(true);
    setGroupFeedback('');
    setError('');

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/messages/group-recipients/${option.value}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const groupRecipients = Array.isArray(res.data?.data) ? res.data.data : [];

      if (groupRecipients.length === 0) {
        setGroupFeedback(`Geen actieve ouderaccounts gevonden in ${option.label}.`);
        return;
      }

      setRecipients((prev) => {
        const byId = new Map(prev.map((recipient) => [recipient.user_id, recipient]));
        groupRecipients.forEach((recipient) => {
          byId.set(recipient.user_id, recipient);
        });
        return [...byId.values()].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );
      });

      setSelectedIds((prev) => [
        ...new Set([...prev, ...groupRecipients.map((recipient) => recipient.user_id)])
      ]);

      setGroupFeedback(
        `${groupRecipients.length} ${groupRecipients.length === 1 ? 'ouder' : 'ouders'} uit ${option.label} toegevoegd.`
      );
    } catch (err) {
      console.error('Kon groepsontvangers niet laden:', err);
      setError(err?.response?.data?.error || 'Kon ouders uit deze groep niet toevoegen');
    } finally {
      setLoadingGroupRecipients(false);
      // De groepskeuze werkt als een veilige "toevoegen"-actie. Ontvangers blijven
      // daarna in het bestaande ontvangersveld staan en kunnen handmatig worden aangepast.
      setSelectedGroupOption(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedIds.length === 0) {
      setError('Selecteer minstens één ontvanger');
      return;
    }

    setSending(true);
    setError('');

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/messages/send`,
        { receiver_ids: selectedIds, subject, body },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      await Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: 'Bericht succesvol verzonden',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });

      navigate('/messages/sent');
    } catch (err) {
      console.error('Fout bij verzenden bericht:', err);
      setError(err?.response?.data?.error || 'Fout bij verzenden bericht');
      setSending(false);
    }
  };

  const selectOptions = recipients.map((user) => ({
    value: user.user_id,
    label: formatRecipientLabel(user),
  }));

  const selectValue = recipients
    .filter((user) => selectedIds.includes(user.user_id))
    .map((user) => ({
      value: user.user_id,
      label: formatRecipientLabel(user),
    }));

  const groupOptions = messageGroups.map((group) => ({
    value: group.groep_id,
    label: [
      group.groep_name,
      group.location_name,
      group.school_year
    ].filter(Boolean).join(' — '),
  }));

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      borderRadius: '10px',
      borderColor: state.isFocused ? '#10a85a' : '#dbe5df',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(16,168,90,0.13)' : 'none',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      '&:hover': { borderColor: '#10a85a' },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e8f8ef',
      borderRadius: '20px',
      padding: '0 4px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#087f49',
      fontSize: '13px',
      fontWeight: 500,
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#087f49',
      borderRadius: '0 20px 20px 0',
      cursor: 'pointer',
      '&:hover': { backgroundColor: '#cfefdc', color: '#075c39' },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#adb5bd',
      fontSize: '14px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#087f49' : state.isFocused ? '#f0faf4' : 'white',
      color: state.isSelected ? 'white' : '#212529',
      fontSize: '14px',
      cursor: 'pointer',
    }),
  };

  return (
    <div className={`nm-container ${visible ? 'nm-visible' : ''}`}>
      <div className="nm-wrapper">
        <button type="button" onClick={() => window.history.back()} className="nm-back-btn">
          <i className="bi bi-arrow-left"></i>
          <span>Terug</span>
        </button>

        <div className="nm-card">
          <div className="nm-card-header">
            <div className="nm-title-icon">
              <i className={`bi ${replyId ? 'bi-reply' : 'bi-pencil-square'}`}></i>
            </div>
            <div>
              <span className="nm-eyebrow">Communicatie</span>
              <h1 className="nm-title">{replyId ? 'Bericht beantwoorden' : 'Nieuw bericht'}</h1>
              <p className="nm-subtitle">
                {replyId
                  ? 'Je antwoord wordt aan het oorspronkelijke bericht gekoppeld.'
                  : 'Stuur een bericht naar een of meerdere ontvangers.'}
              </p>
            </div>
          </div>

          <div className="nm-card-body">
            {error && (
              <div className="nm-error">
                <i className="bi bi-exclamation-circle me-2"></i>{error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {!replyId && groupOptions.length > 0 && (
                <div className="nm-field nm-group-helper">
                  <label className="nm-label" htmlFor="message-group-recipients">
                    Ouders uit een groep toevoegen
                  </label>
                  <Select
                    inputId="message-group-recipients"
                    options={groupOptions}
                    value={selectedGroupOption}
                    onChange={handleGroupSelect}
                    isLoading={loadingGroupRecipients}
                    isDisabled={loadingGroupRecipients}
                    placeholder="Zoek en kies een groep..."
                    styles={selectStyles}
                    noOptionsMessage={() => 'Geen groepen gevonden'}
                  />
                  <p className="nm-field-hint">
                    Kies een groep om alle actieve ouders van de actieve leerlingen aan de ontvangers toe te voegen. Dubbele ouders worden automatisch maar één keer geselecteerd.
                  </p>
                  {groupFeedback && (
                    <div className="nm-group-feedback" role="status">
                      <i className="bi bi-check-circle me-2"></i>{groupFeedback}
                    </div>
                  )}
                </div>
              )}

              <div className="nm-field">
                <label className="nm-label" htmlFor="message-recipients">
                  Ontvangers <span className="nm-required">*</span>
                </label>
                <Select
                  inputId="message-recipients"
                  isMulti
                  options={selectOptions}
                  value={selectValue}
                  onChange={(selected) => setSelectedIds((selected || []).map((o) => o.value))}
                  placeholder="Zoek en selecteer ontvangers..."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={selectStyles}
                  noOptionsMessage={() => 'Geen ontvangers gevonden'}
                />
              </div>

              <div className="nm-field">
                <label className="nm-label" htmlFor="message-subject">Onderwerp</label>
                <input
                  type="text"
                  id="message-subject"
                  className="nm-input"
                  placeholder="Onderwerp van het bericht"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>

              <div className="nm-field">
                <label className="nm-label" htmlFor="message-body">
                  Bericht <span className="nm-required">*</span>
                </label>
                <textarea
                  className="nm-textarea"
                  id="message-body"
                  rows="6"
                  placeholder="Schrijf hier je bericht..."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  required
                />
              </div>

              <div className="nm-actions">
                <button
                  type="button"
                  className="nm-cancel-btn"
                  onClick={() => window.history.back()}
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  className="nm-send-btn"
                  disabled={sending}
                >
                  {sending ? (
                    <>
                      <span className="nm-spinner"></span>
                      Verzenden...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send me-2"></i>Verzenden
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewMessage;
